<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'MaterialTabs',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'input']);

  const props = defineProps({
    ...userComponentProps,
    title: {
      type: String,
      required: false,
    },
    subtitle: {
      type: String,
      required: false,
    },
    parentText: {
      type: String,
      required: false,
    },
    parentTo: {
      type: Object,
      required: false,
    },
    closeButton: {
      type: Boolean,
      required: false,
    },
    tabManager: {
      type: Object,
      required: false,
    },
    noContentWrapper: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const contentClass = computed(() => {
    return props.noContentWrapper
      ? 'flex-fill overflow-hidden h-100'
      : 'flex-fill overflow-auto px-3 py-4 h-100 bg-page';
  });
  const hasBreadcrumb = computed(() => {
    return !!props.parentText;
  });
  const hasTitleRow = computed(() => {
    return !!props.title;
  });

  watch(
    () => props.title,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('title');
    }
  );
  watch(
    () => props.subtitle,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('subtitle');
    }
  );
  watch(
    () => props.parentText,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('parentText');
    }
  );
  watch(
    () => props.parentTo,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('parentTo');
    }
  );
  watch(
    () => props.closeButton,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('closeButton');
    }
  );
  watch(
    () => props.tabManager,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('tabManager');
    }
  );
  watch(
    () => props.noContentWrapper,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('noContentWrapper');
    }
  );

  const act_5962862420439144_emit_9c1amruyn_getActionArgs = async ($event) => {
    return {
      event: 'input',
      value: {
        tabIndex: $event.originalEvent.newTabIndex,
      },
    };
  };
  const act_5962862420439144_script_2vexzn454_value_callable = async (
    $event
  ) => {
    return props.tabManager.visited($event.originalEvent.newTabIndex, true);
  };
  const act_5962862420439144_script_2vexzn454_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_2vexzn454_value_callable($event);
      },
    };
  };
  const act_5962862420439144_script_2vexzn454_getWhen = async ($event) => {
    return !!props.tabManager;
  };
  const act_648747096704084_emit_3r55mcwq2_getActionArgs = async ($event) => {
    return {
      event: 'close',
    };
  };

  const act_5962862420439144_emit_9c1amruyn = new actionTypes.EmitAction({
    actionArgs: act_5962862420439144_emit_9c1amruyn_getActionArgs,
  });
  const inst_evh_5962862420439144_activateTab_4lt49hfnw =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_emit_9c1amruyn,
      event: 'activate-tab',
      displayName: 'emit',
    });
  const act_5962862420439144_script_2vexzn454 = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_2vexzn454_getActionArgs,
    when: act_5962862420439144_script_2vexzn454_getWhen,
  });
  const inst_evh_5962862420439144_activateTab_gn7rb1is1 =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_2vexzn454,
      event: 'activate-tab',
      displayName: 'script',
    });
  const inst_evh_5962862420439144_activateTab =
    new actionTypes.EventHandlerGroup({
      handlers: [
        inst_evh_5962862420439144_activateTab_4lt49hfnw,
        inst_evh_5962862420439144_activateTab_gn7rb1is1,
      ],
      event: 'activate-tab',
    });
  const act_648747096704084_emit_3r55mcwq2 = new actionTypes.EmitAction({
    actionArgs: act_648747096704084_emit_3r55mcwq2_getActionArgs,
  });
  const inst_evh_648747096704084_click_ezgkjuga8 =
    new actionTypes.EventHandlerImpl({
      action: act_648747096704084_emit_3r55mcwq2,
      event: 'click',
      displayName: 'emit',
    });
  const inst_evh_648747096704084_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_648747096704084_click_ezgkjuga8],
    event: 'click',
  });

  const actions = {
    inst_evh_5962862420439144_activateTab_4lt49hfnw,
    inst_evh_5962862420439144_activateTab_gn7rb1is1,
    inst_evh_5962862420439144_activateTab,
    inst_evh_648747096704084_click_ezgkjuga8,
    inst_evh_648747096704084_click,
  };

  const evh_5962862420439144_activateTab_4lt49hfnw = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_activateTab_4lt49hfnw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_activateTab_gn7rb1is1 = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_activateTab_gn7rb1is1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_activateTab = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_activateTab.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_648747096704084_click_ezgkjuga8 = async (event, scope) => {
    return await actions.inst_evh_648747096704084_click_ezgkjuga8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_648747096704084_click = async (event, scope) => {
    return await actions.inst_evh_648747096704084_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <div class="detail-page d-flex flex-column h-100 overflow-hidden">
    <div
      class="detail-page__header bg-white px-3 pt-3 pb-2"
      v-if="hasTitleRow || hasBreadcrumb"
    >
      <div
        class="detail-page__breadcrumb d-flex align-items-center small text-muted mb-1"
        v-if="hasBreadcrumb"
      >
        <router-link
          :to="parentTo"
          class="detail-page__breadcrumb-link text-decoration-none text-muted"
        >
          {{ parentText }}
        </router-link>
        <ItpIcon
          name="mdi:chevron-right"
          class="detail-page__breadcrumb-sep ms-1"
        >
        </ItpIcon>
      </div>
      <div
        class="detail-page__title-row d-flex align-items-baseline mb-2"
        v-if="hasTitleRow"
      >
        <h1 class="detail-page__title h4 mb-0 me-3">
          {{ title }}
        </h1>
        <span class="detail-page__subtitle text-muted me-3" v-if="subtitle">
          {{ subtitle }}
        </span>
        <div class="detail-page__title-badges d-flex align-items-center gap-2">
          <slot name="title-badges"> </slot>
        </div>
        <div class="detail-page__meta ms-auto d-flex align-items-center gap-3">
          <slot name="meta"> </slot>
        </div>
      </div>
      <div
        class="detail-page__actions d-flex align-items-center flex-wrap gap-2"
      >
        <slot name="actions"> </slot>
      </div>
    </div>
    <BTabs
      nav-class="material px-3"
      :content-class="contentClass"
      class="detail-page__tabs flex-grow-1 d-flex flex-column tabs-material overflow-hidden"
      @activate-tab="evh_5962862420439144_activateTab($event, {})"
    >
      <slot> </slot>
      <template #tabs-start>
        <slot name="tabs-start"> </slot>
      </template>
      <template #tabs-end v-if="closeButton">
        <button
          data-bs-dismiss="modal"
          aria-label="Close"
          class="close ms-auto me-2"
          @click="evh_648747096704084_click($event, {})"
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </template>
    </BTabs>
  </div>
</template>
